import type { DateFilter, DatePreset } from './types'

export const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_year', label: 'This year' },
  { value: 'all', label: 'All time' },
]

const PRESET_LABEL = new Map(DATE_PRESETS.map((p) => [p.value, p.label]))

const MS_PER_DAY = 86_400_000

// All Date values produced/consumed by this filter represent a UTC calendar
// day (midnight UTC). The ClickHouse-backed insights API buckets rows by UTC
// date, so the picker must speak the same calendar.
const utcDayFromLocal = (d: Date): Date => new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))

const todayUtc = (now: Date): Date => new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))

export const utcToday = (): Date => todayUtc(new Date())

export const resolvePreset = (preset: DatePreset, now = new Date()): { start?: Date; end?: Date } => {
  const today = todayUtc(now)
  const y = today.getUTCFullYear()
  const m = today.getUTCMonth()
  switch (preset) {
    case 'all':
      return {}
    case '7d':
      return { start: new Date(today.getTime() - 6 * MS_PER_DAY), end: today }
    case '30d':
      return { start: new Date(today.getTime() - 29 * MS_PER_DAY), end: today }
    case 'this_month':
      return { start: new Date(Date.UTC(y, m, 1)), end: today }
    case 'last_month':
      return {
        start: new Date(Date.UTC(y, m - 1, 1)),
        end: new Date(Date.UTC(y, m, 0)),
      }
    case 'this_year':
      // End at `now` (not endOfYear) so the prior period is an
      // equal-length YTD window before Jan 1, not a full prior year.
      // Otherwise pct_change compares partial-year data against
      // full-year data and shows a phantom drop.
      return { start: new Date(Date.UTC(y, 0, 1)), end: today }
  }
}

export const resolveDateFilter = (filter: DateFilter): { start?: Date; end?: Date } => {
  if (filter.kind === 'preset') return resolvePreset(filter.preset)
  return { start: filter.start, end: filter.end }
}

// Normalize a Date picked from a calendar widget (interpreted in the user's
// local timezone) into the UTC calendar day the user selected on screen.
export const normalizeCalendarDateToUtc = (d: Date): Date => utcDayFromLocal(d)

export const formatDateParam = (d: Date): string => d.toISOString().slice(0, 10)

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export const formatUtcDateLabel = (d: Date): string =>
  `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`

export const dateFilterLabel = (filter: DateFilter): string => {
  if (filter.kind === 'preset') return PRESET_LABEL.get(filter.preset) ?? 'All time'
  return `${formatUtcDateLabel(filter.start)} – ${formatUtcDateLabel(filter.end)}`
}

// True when the filter has a ``start_date`` — the backend computes pct_change
// against the equivalent prior duration. Without a start_date (pure lifetime),
// pct_change falls back to fixed L7-vs-L7 and the label should reflect that.
export const hasEquivalentPrior = (filter: DateFilter): boolean => {
  const { start } = resolveDateFilter(filter)
  return start !== undefined
}

// Wording is friendlier than literal: "This month" with 11 days elapsed compares
// against the 11 days before May 1, not all of April — but "from last month"
// matches how users read the pill.
const PRESET_PRIOR_LABEL: Record<DatePreset, string> = {
  all: 'from prior 7 days',
  '7d': 'from previous 7 days',
  '30d': 'from previous 30 days',
  this_month: 'from last month',
  last_month: 'from prior month',
  this_year: 'from last year',
}

export const priorPeriodLabel = (filter: DateFilter): string => {
  if (filter.kind === 'preset') return PRESET_PRIOR_LABEL[filter.preset]
  return hasEquivalentPrior(filter) ? 'from prior period' : 'from prior 7 days'
}
